import { useEffect, useMemo, useState } from "react";
import { Bot, Copy, MailCheck, PauseCircle, Plus, Send, ShieldOff, Sparkles } from "lucide-react";
import useSeo from "../hooks/useSeo";
import { api } from "../lib/api";

const prospectInitial = {
  prospect_type: "employer",
  first_name: "",
  last_name: "",
  email: "",
  company: "",
  title: "",
  industry: "",
  location: "",
  linkedin_url: "",
  source: "Manual",
  pain_point: "",
  notes: "",
};

const campaignInitial = {
  name: "Employer discovery sequence",
  audience: "Employers",
  goal: "Start conversations with companies that need help posting jobs, sourcing candidates, or hiring project/program management talent.",
  service_focus: "job posting support, candidate sourcing, and project/program management hiring",
  tone: "Professional, concise, consultative",
  cta: "Book a 15-minute TalentBridge consultation",
  sequence_count: 3,
};

function Field({ label, name, value, onChange, type = "text", textarea = false, required = true, testPrefix }) {
  const Control = textarea ? "textarea" : "input";
  return (
    <label className="grid gap-2" data-testid={`${testPrefix}-${name}-field`}>
      <span className="text-xs font-bold uppercase tracking-[0.16em] text-[#64748B]" data-testid={`${testPrefix}-${name}-label`}>{label}</span>
      <Control
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        className="min-h-11 border border-[#CBD5E1] bg-white px-3 py-2 text-sm text-[#0B1120] focus:outline-none focus:ring-2 focus:ring-[#0066FF]"
        data-testid={`${testPrefix}-${name}-input`}
      />
    </label>
  );
}

function MiniStat({ icon: Icon, label, value, testId }) {
  return (
    <div className="border border-[#E2E8F0] bg-white p-4" data-testid={testId}>
      <Icon className="h-5 w-5 text-[#0066FF]" />
      <p className="mt-4 font-heading text-2xl font-black text-[#0A2540]" data-testid={`${testId}-value`}>{value}</p>
      <p className="mt-1 text-xs font-bold uppercase tracking-[0.14em] text-[#64748B]" data-testid={`${testId}-label`}>{label}</p>
    </div>
  );
}

export default function ColdEmailAgent() {
  useSeo("Cold Email Agent", "AI-powered TalentBridge cold email workspace for prospects, campaigns, drafts, queue tracking, and opt-outs.");
  const [overview, setOverview] = useState(null);
  const [prospects, setProspects] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [queue, setQueue] = useState([]);
  const [optOuts, setOptOuts] = useState([]);
  const [providerStatus, setProviderStatus] = useState(null);
  const [prospectForm, setProspectForm] = useState(prospectInitial);
  const [campaignForm, setCampaignForm] = useState(campaignInitial);
  const [selectedProspect, setSelectedProspect] = useState("");
  const [selectedCampaign, setSelectedCampaign] = useState("");
  const [stepNumber, setStepNumber] = useState(1);
  const [angle, setAngle] = useState("");
  const [optOutEmail, setOptOutEmail] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const [overviewRes, prospectsRes, campaignsRes, draftsRes, queueRes, optOutsRes, providerRes] = await Promise.all([
      api.coldEmailOverview(),
      api.prospects(),
      api.campaigns(),
      api.coldEmailDrafts(),
      api.emailQueue(),
      api.optOuts(),
      api.emailProviderStatus(),
    ]);
    setOverview(overviewRes.data);
    setProspects(prospectsRes.data);
    setCampaigns(campaignsRes.data);
    setDrafts(draftsRes.data);
    setQueue(queueRes.data);
    setOptOuts(optOutsRes.data);
    setProviderStatus(providerRes.data);
    if (!selectedProspect && prospectsRes.data[0]) setSelectedProspect(prospectsRes.data[0].id);
    if (!selectedCampaign && campaignsRes.data[0]) setSelectedCampaign(campaignsRes.data[0].id);
  };

  useEffect(() => {
    load();
  }, []);

  const activeProspects = useMemo(() => prospects.filter((item) => item.status !== "Opted Out"), [prospects]);

  const submitProspect = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const response = await api.createProspect(prospectForm);
      setSelectedProspect(response.data.id);
      setProspectForm(prospectInitial);
      setStatus("Prospect added.");
      await load();
    } finally {
      setLoading(false);
    }
  };

  const submitCampaign = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const response = await api.createCampaign({ ...campaignForm, sequence_count: Number(campaignForm.sequence_count) });
      setSelectedCampaign(response.data.id);
      setStatus("Campaign created.");
      await load();
    } finally {
      setLoading(false);
    }
  };

  const generateDraft = async () => {
    if (!selectedProspect || !selectedCampaign) {
      setStatus("Add or select a prospect and campaign first.");
      return;
    }
    setLoading(true);
    try {
      await api.generateColdEmail({ prospect_id: selectedProspect, campaign_id: selectedCampaign, step_number: Number(stepNumber), angle });
      setStatus("AI draft generated.");
      await load();
    } finally {
      setLoading(false);
    }
  };

  const queueDraft = async (draftId) => {
    await api.queueEmail({ draft_id: draftId, scheduled_for: "Next available manual send window" });
    setStatus("Draft added to send queue.");
    await load();
  };

  const updateStatus = async (id, value) => {
    await api.updateQueueStatus(id, value);
    setStatus(`Queue item marked ${value}.`);
    await load();
  };

  const sendNow = async (id) => {
    setLoading(true);
    try {
      const response = await api.sendQueuedEmail(id);
      setStatus(response.data.message);
      await load();
    } catch (error) {
      setStatus(error.response?.data?.detail || "Email delivery failed. Check provider settings.");
      await load();
    } finally {
      setLoading(false);
    }
  };

  const safeCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setStatus("Copied to clipboard.");
    } catch (error) {
      setStatus("Copy blocked by browser permissions. Select the text manually to copy.");
    }
  };

  const submitOptOut = async (event) => {
    event.preventDefault();
    await api.createOptOut({ email: optOutEmail, reason: "Manual opt-out" });
    setOptOutEmail("");
    setStatus("Opt-out saved and matching prospects suppressed.");
    await load();
  };

  const pChange = (event) => setProspectForm({ ...prospectForm, [event.target.name]: event.target.value });
  const cChange = (event) => setCampaignForm({ ...campaignForm, [event.target.name]: event.target.value });

  return (
    <div className="grid gap-8" data-testid="cold-email-agent-page">
      <section className="border border-[#E2E8F0] bg-[#0A2540] p-6 text-white" data-testid="cold-email-hero-section">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-200" data-testid="cold-email-eyebrow">Cold email agent</p>
            <h1 className="mt-3 font-heading text-3xl font-black" data-testid="cold-email-title">AI outreach command center</h1>
            <p className="mt-3 max-w-3xl text-sm leading-7 text-slate-200" data-testid="cold-email-description">
              Manage prospects, generate personalized recruitment outreach, queue manual sends, track replies, and maintain opt-outs from one workspace.
            </p>
          </div>
          <button onClick={generateDraft} disabled={loading} className="inline-flex items-center justify-center gap-2 bg-[#0066FF] px-5 py-3 text-sm font-bold text-white hover:bg-[#0052CC] disabled:opacity-60" data-testid="cold-email-generate-top-button">
            <Sparkles className="h-4 w-4" /> {loading ? "Working..." : "Generate draft"}
          </button>
        </div>
      </section>

      {status && <div className="border border-emerald-200 bg-emerald-50 p-3 text-sm font-bold text-emerald-800" data-testid="cold-email-status-message">{status}</div>}

      <section className={`border p-4 ${providerStatus?.configured ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`} data-testid="cold-provider-status-panel">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className={`text-xs font-bold uppercase tracking-[0.18em] ${providerStatus?.configured ? "text-emerald-700" : "text-amber-700"}`} data-testid="cold-provider-status-eyebrow">
              {providerStatus?.configured ? "Real sending enabled" : "Provider setup required"}
            </p>
            <p className="mt-1 text-sm font-semibold text-[#0A2540]" data-testid="cold-provider-status-text">
              Provider: {providerStatus?.provider || "not configured"} · Sender: {providerStatus?.sender_email || "not configured"}
            </p>
            {!providerStatus?.configured && (
              <p className="mt-2 text-sm text-[#475569]" data-testid="cold-provider-missing-fields-text">
                Add these backend settings to enable real sending: {(providerStatus?.missing_fields || []).join(", ") || "EMAIL_PROVIDER and SENDER_EMAIL"}.
              </p>
            )}
          </div>
          <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#64748B]" data-testid="cold-provider-supported-text">
            Supports Resend · SendGrid · SMTP
          </p>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-7" data-testid="cold-email-stats-section">
        <MiniStat icon={Bot} label="Prospects" value={overview?.prospect_count ?? 0} testId="cold-stat-prospects" />
        <MiniStat icon={Sparkles} label="Campaigns" value={overview?.campaign_count ?? 0} testId="cold-stat-campaigns" />
        <MiniStat icon={Copy} label="Drafts" value={overview?.draft_count ?? 0} testId="cold-stat-drafts" />
        <MiniStat icon={Send} label="Queued" value={overview?.queued_count ?? 0} testId="cold-stat-queued" />
        <MiniStat icon={MailCheck} label="Sent" value={overview?.sent_count ?? 0} testId="cold-stat-sent" />
        <MiniStat icon={MailCheck} label="Replied" value={overview?.replied_count ?? 0} testId="cold-stat-replied" />
        <MiniStat icon={ShieldOff} label="Opt-outs" value={overview?.opted_out_count ?? 0} testId="cold-stat-optouts" />
      </section>

      <section className="grid gap-6 xl:grid-cols-2" data-testid="cold-email-setup-section">
        <form onSubmit={submitProspect} className="grid gap-4 border border-[#E2E8F0] bg-white p-5" data-testid="cold-prospect-form">
          <h2 className="font-heading text-xl font-black text-[#0A2540]" data-testid="cold-prospect-form-title">Add prospect</h2>
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="First name" name="first_name" value={prospectForm.first_name} onChange={pChange} testPrefix="cold-prospect" />
            <Field label="Last name" name="last_name" value={prospectForm.last_name} onChange={pChange} testPrefix="cold-prospect" />
            <Field label="Email" name="email" type="email" value={prospectForm.email} onChange={pChange} testPrefix="cold-prospect" />
            <Field label="Company" name="company" value={prospectForm.company} onChange={pChange} required={false} testPrefix="cold-prospect" />
            <Field label="Title" name="title" value={prospectForm.title} onChange={pChange} required={false} testPrefix="cold-prospect" />
            <Field label="Industry" name="industry" value={prospectForm.industry} onChange={pChange} required={false} testPrefix="cold-prospect" />
          </div>
          <Field label="Pain point" name="pain_point" value={prospectForm.pain_point} onChange={pChange} required={false} textarea testPrefix="cold-prospect" />
          <button type="submit" disabled={loading} className="inline-flex items-center justify-center gap-2 bg-[#0A2540] px-5 py-3 font-bold text-white hover:bg-[#051424] disabled:opacity-60" data-testid="cold-prospect-submit-button"><Plus className="h-4 w-4" /> Add prospect</button>
        </form>

        <form onSubmit={submitCampaign} className="grid gap-4 border border-[#E2E8F0] bg-white p-5" data-testid="cold-campaign-form">
          <h2 className="font-heading text-xl font-black text-[#0A2540]" data-testid="cold-campaign-form-title">Campaign brief</h2>
          <Field label="Campaign name" name="name" value={campaignForm.name} onChange={cChange} testPrefix="cold-campaign" />
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="Audience" name="audience" value={campaignForm.audience} onChange={cChange} testPrefix="cold-campaign" />
            <Field label="Sequence steps" name="sequence_count" type="number" value={campaignForm.sequence_count} onChange={cChange} testPrefix="cold-campaign" />
          </div>
          <Field label="Goal" name="goal" value={campaignForm.goal} onChange={cChange} textarea testPrefix="cold-campaign" />
          <Field label="Service focus" name="service_focus" value={campaignForm.service_focus} onChange={cChange} testPrefix="cold-campaign" />
          <Field label="CTA" name="cta" value={campaignForm.cta} onChange={cChange} testPrefix="cold-campaign" />
          <button type="submit" disabled={loading} className="inline-flex items-center justify-center gap-2 bg-[#0066FF] px-5 py-3 font-bold text-white hover:bg-[#0052CC] disabled:opacity-60" data-testid="cold-campaign-submit-button"><Plus className="h-4 w-4" /> Save campaign</button>
        </form>
      </section>

      <section className="border border-[#E2E8F0] bg-white p-5" data-testid="cold-generator-section">
        <h2 className="font-heading text-xl font-black text-[#0A2540]" data-testid="cold-generator-title">Personalization generator</h2>
        <div className="mt-5 grid gap-4 lg:grid-cols-[1fr_1fr_0.35fr]">
          <select value={selectedProspect} onChange={(event) => setSelectedProspect(event.target.value)} className="h-11 border border-[#CBD5E1] bg-white px-3 text-sm" data-testid="cold-selected-prospect-input" aria-label="Select prospect">
            <option value="" label="Select prospect" />
            {activeProspects.map((prospect) => (
              <option
                key={prospect.id}
                value={prospect.id}
                label={`${prospect.first_name} ${prospect.last_name} · ${prospect.company || prospect.email}`}
              />
            ))}
          </select>
          <select value={selectedCampaign} onChange={(event) => setSelectedCampaign(event.target.value)} className="h-11 border border-[#CBD5E1] bg-white px-3 text-sm" data-testid="cold-selected-campaign-input" aria-label="Select campaign">
            <option value="" label="Select campaign" />
            {campaigns.map((campaign) => <option key={campaign.id} value={campaign.id} label={campaign.name} />)}
          </select>
          <input type="number" min="1" max="5" value={stepNumber} onChange={(event) => setStepNumber(event.target.value)} className="h-11 border border-[#CBD5E1] bg-white px-3 text-sm" data-testid="cold-step-number-input" aria-label="Step number" />
        </div>
        <textarea value={angle} onChange={(event) => setAngle(event.target.value)} placeholder="Optional angle: e.g. hiring speed, hard-to-fill role, candidate quality" className="mt-4 min-h-24 w-full border border-[#CBD5E1] bg-white px-3 py-2 text-sm" data-testid="cold-angle-input" />
        <button onClick={generateDraft} disabled={loading} className="mt-4 inline-flex items-center gap-2 bg-[#0A2540] px-5 py-3 font-bold text-white hover:bg-[#051424] disabled:opacity-60" data-testid="cold-generate-draft-button"><Sparkles className="h-4 w-4" /> {loading ? "Generating..." : "Generate AI email"}</button>
      </section>

      <section className="grid gap-6 xl:grid-cols-2" data-testid="cold-output-section">
        <div className="border border-[#E2E8F0] bg-[#FBFBFC] p-5" data-testid="cold-drafts-panel">
          <h2 className="font-heading text-xl font-black text-[#0A2540]" data-testid="cold-drafts-title">Drafts</h2>
          <div className="mt-5 grid gap-4">
            {drafts.length === 0 && <p className="text-sm text-[#64748B]" data-testid="cold-empty-drafts-message">No drafts yet.</p>}
            {drafts.map((draft) => (
              <article key={draft.id} className="border border-[#E2E8F0] bg-white p-4" data-testid={`cold-draft-${draft.id}`}>
                <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#0066FF]" data-testid={`cold-draft-step-${draft.id}`}>Step {draft.step_number} · {draft.status}</p>
                <h3 className="mt-2 font-heading text-lg font-black text-[#0A2540]" data-testid={`cold-draft-subject-${draft.id}`}>{draft.subject}</h3>
                <p className="mt-2 text-sm font-semibold text-[#64748B]" data-testid={`cold-draft-preview-${draft.id}`}>{draft.preview_text}</p>
                <p className="mt-4 whitespace-pre-line text-sm leading-7 text-[#334155]" data-testid={`cold-draft-body-${draft.id}`}>{draft.body}</p>
                <p className="mt-4 border-l-4 border-[#0066FF] bg-[#F8FAFC] p-3 text-xs text-[#475569]" data-testid={`cold-draft-notes-${draft.id}`}>{draft.personalization_notes}</p>
                <div className="mt-4 flex flex-wrap gap-3">
                  <button onClick={() => safeCopy(`${draft.subject}\n\n${draft.body}`)} className="inline-flex items-center gap-2 border border-[#CBD5E1] px-3 py-2 text-sm font-bold text-[#0A2540]" data-testid={`cold-draft-copy-button-${draft.id}`}><Copy className="h-4 w-4" /> Copy</button>
                  <button onClick={() => queueDraft(draft.id)} className="inline-flex items-center gap-2 bg-[#0066FF] px-3 py-2 text-sm font-bold text-white" data-testid={`cold-draft-queue-button-${draft.id}`}><Send className="h-4 w-4" /> Queue</button>
                </div>
              </article>
            ))}
          </div>
        </div>

        <div className="border border-[#E2E8F0] bg-[#FBFBFC] p-5" data-testid="cold-queue-panel">
          <h2 className="font-heading text-xl font-black text-[#0A2540]" data-testid="cold-queue-title">Send queue & tracking</h2>
          <div className="mt-5 grid gap-4">
            {queue.length === 0 && <p className="text-sm text-[#64748B]" data-testid="cold-empty-queue-message">No queued emails yet.</p>}
            {queue.map((item) => (
              <article key={item.id} className="border border-[#E2E8F0] bg-white p-4" data-testid={`cold-queue-item-${item.id}`}>
                <p className="font-heading text-lg font-black text-[#0A2540]" data-testid={`cold-queue-subject-${item.id}`}>{item.subject}</p>
                <p className="mt-2 text-xs font-bold uppercase tracking-[0.14em] text-[#64748B]" data-testid={`cold-queue-mode-${item.id}`}>{item.delivery_mode} · {item.status}</p>
                {item.recipient_email && <p className="mt-2 text-sm text-[#475569]" data-testid={`cold-queue-recipient-${item.id}`}>To: {item.recipient_email}</p>}
                {item.provider_message_id && <p className="mt-2 text-xs text-emerald-700" data-testid={`cold-queue-provider-id-${item.id}`}>Provider ID: {item.provider_message_id}</p>}
                {item.provider_error && <p className="mt-2 border border-red-200 bg-red-50 p-2 text-xs text-red-700" data-testid={`cold-queue-provider-error-${item.id}`}>{item.provider_error}</p>}
                <select value={item.status} onChange={(event) => updateStatus(item.id, event.target.value)} className="mt-4 w-full border border-[#CBD5E1] bg-white px-3 py-2 text-sm font-semibold" data-testid={`cold-queue-status-select-${item.id}`} aria-label="Queue status">
                  {['Queued', 'Sent', 'Opened', 'Replied', 'Bounced', 'Paused'].map((statusOption) => <option key={statusOption} value={statusOption} label={statusOption} />)}
                </select>
                <button onClick={() => sendNow(item.id)} disabled={loading || item.status === "Sent" || !providerStatus?.configured} className="mt-3 inline-flex items-center gap-2 bg-[#0A2540] px-4 py-2 text-sm font-bold text-white hover:bg-[#051424] disabled:opacity-50" data-testid={`cold-queue-send-now-button-${item.id}`}>
                  <Send className="h-4 w-4" /> Send now
                </button>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="grid gap-6 xl:grid-cols-[0.7fr_1.3fr]" data-testid="cold-compliance-section">
        <form onSubmit={submitOptOut} className="border border-[#E2E8F0] bg-white p-5" data-testid="cold-optout-form">
          <h2 className="font-heading text-xl font-black text-[#0A2540]" data-testid="cold-optout-title">Opt-out suppression</h2>
          <p className="mt-2 text-sm leading-6 text-[#64748B]" data-testid="cold-optout-description">Add an email to suppress future outreach and mark matching prospects as opted out.</p>
          <input type="email" value={optOutEmail} onChange={(event) => setOptOutEmail(event.target.value)} required className="mt-4 h-11 w-full border border-[#CBD5E1] px-3" data-testid="cold-optout-email-input" aria-label="Opt-out email" />
          <button type="submit" className="mt-4 inline-flex items-center gap-2 bg-[#0A2540] px-5 py-3 font-bold text-white" data-testid="cold-optout-submit-button"><PauseCircle className="h-4 w-4" /> Suppress email</button>
        </form>
        <div className="border border-[#E2E8F0] bg-white p-5" data-testid="cold-optout-list-panel">
          <h2 className="font-heading text-xl font-black text-[#0A2540]" data-testid="cold-optout-list-title">Suppression list</h2>
          <div className="mt-4 grid gap-2">
            {optOuts.length === 0 && <p className="text-sm text-[#64748B]" data-testid="cold-empty-optouts-message">No opt-outs yet.</p>}
            {optOuts.map((item) => <p key={item.id} className="border border-[#E2E8F0] p-3 text-sm font-semibold text-[#0A2540]" data-testid={`cold-optout-item-${item.id}`}>{item.email}</p>)}
          </div>
        </div>
      </section>
    </div>
  );
}